import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { AgGridConfig, RequestStatus } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { Entity, SubAccount } from '@approot/models/trade-capture';
import {
  ConfigService,
  EntityMapperService,
  LegService,
  TradesCaptureService
} from '@approot/services';
import { ReusableGridTemplateComponent } from '@approot/shared/custom-components/ag-grid/reusable-grid-template/reusable-grid-template.component';
import { TextBoxEditorComponent } from '@approot/shared/custom-components/ag-grid/text-box-editor/text-box-editor.component';
import { clone, isTrueLike, SubscriptionSink } from '@approot/utilities';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  SuppressKeyboardEventParams
} from 'ag-grid-community';
import { isEqual } from 'lodash';
import { forkJoin } from 'rxjs';
import { SubAccountsComponent } from './sub-accounts/sub-accounts.component';
@Component({
  selector: 'app-entities',
  templateUrl: './entities.component.html',
  styleUrls: ['./entities.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntitiesComponent implements OnInit, OnDestroy {
  @ViewChild('entityComponent', { static: true })
  public entityComponent: TemplateRef<any>;
  @ViewChild('entitySubComponent', { static: true })
  public entitySubComponent: TemplateRef<any>;
  public subAccountsComponent = SubAccountsComponent;
  public entitiesForm: FormGroup;
  public loadingEntities = true;
  public savingEntitiesStatus: RequestStatus = '';
  public subacctOpen = new Map<number, boolean>();
  public formChanged = false;
  public columnDefs: Header[];
  public groupDefaultExpanded = 0;
  public detailCellRendererParams;
  public isRowMaster = dataItem => {
    return dataItem.subAccounts?.length ? true : false;
  };
  public selectedEntity: Entity;
  public defaultColDef: ColDef = {
    suppressKeyboardEvent: suppressNavigation
  };
  public allowAddEntity = false;
  public allowDeleteEntity = false;
  public readonly = true;
  private entitiesToDelete = [];
  private subsink = new SubscriptionSink();
  private initialFormValue: any = null;
  private gridApi: GridApi<Entity>;
  private subAccountsHeaders: Header[];
  private originalHeaders: Header[];
  @ViewChild('divToScroll', { static: false }) private divToScroll: ElementRef;
  constructor(
    private tradesCaptureService: TradesCaptureService,
    private legService: LegService,
    private changeRef: ChangeDetectorRef,
    private configService: ConfigService,
    private entityMapper: EntityMapperService
  ) {
    effect(() => {
      const settings = this.configService.userSettings();
      this.readonly = !!settings.parent.entitiesReadOnly;
      this.allowAddEntity =
        !this.readonly && settings.userPolicies.includes('entity:write');
      this.allowDeleteEntity =
        !this.readonly && settings.userPolicies.includes('entity:delete');
      if (this.columnDefs && this.readonly) {
        this.columnDefs.forEach(col => {
          col.editable = !this.readonly;
        });
      }

      this.handleMainHeaders(settings.ag_grid_config, this.readonly);
      this.handleSubAccountsHeaders(settings.ag_grid_config);
      this.handleDetailCellRendererParams();
    });
  }

  public ngOnInit() {
    this.entitiesForm = new FormGroup({
      entities: new FormArray([])
    });

    this.subsink.sink = this.entitiesForm.valueChanges.subscribe(() => {
      this.formChanged = !isEqual(
        this.initialFormValue,
        this.entitiesForm.getRawValue()
      );
    });
  }

  public ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  public createEntityForm(entity: Entity): FormGroup {
    let subAccounts = new FormArray(
      entity?.subAccounts?.map(subAccount =>
        this.subAccountToFormControl(subAccount, this.subAccountsHeaders)
      ) || []
    );

    const entityGroup = { subAccounts };

    this.originalHeaders.forEach(x => {
      if (!x.field) {
        return;
      }
      const validators = [];
      if (x.optional === false) {
        validators.push(Validators.required);
      }

      if (x.field === 'identifier') {
        validators.push(this.identifierValidator.bind(this));
      }

      const currentValue = entity?.[x.field];
      let value = null;
      switch (x.valueFormatter) {
        case 'boolean':
          value = isTrueLike(currentValue);
          break;
        default:
          value = currentValue ?? '';
          break;
      }
      entityGroup[x.field] = new FormControl(value, validators);
    });

    const entityForm = new FormGroup(entityGroup);
    if (entity?.identifier) {
      entityForm.get('identifier').disable();
    }
    return entityForm;
  }

  public get entitiesArray() {
    return (this.entitiesForm.get('entities') as FormArray).controls;
  }

  public subAccountsArray(i: number): any[] {
    return (this.entitiesArray[i].get('subAccounts') as FormArray).controls;
  }

  public addNewEntity(): void {
    const newForm = this.createEntityForm(null);
    const entities = this.entitiesForm.get('entities') as FormArray;
    entities.push(newForm);
    this.formChanged = true;
    const latestRowIndex = entities.length - 1;

    this.gridApi.applyTransaction({
      add: [{ ...clone(newForm.getRawValue()), rowIndex: latestRowIndex }]
    });

    this.gridApi.forEachNode(node => {
      if (node.data.rowIndex === latestRowIndex) {
        this.gridApi.ensureNodeVisible(node);
        return;
      }
    });
  }

  public saveChanges() {
    // get all the rows that have been altered
    const changedRows = (
      this.entitiesForm.get('entities') as FormArray
    ).controls.filter(entity => entity.dirty === true);

    let changes = {};

    changedRows.forEach((row: FormGroup, index: number) => {
      const item = row.getRawValue() as Entity;
      if (item.subAccounts?.length === 0) {
        delete item.subAccounts;
      }
      changes['save' + index] = this.tradesCaptureService.setEntity(item);
    });

    this.entitiesToDelete.forEach((row: any, index: number) => {
      changes['delete' + index] = this.tradesCaptureService.deleteEntity(
        row.identifier
      );
    });
    this.savingEntitiesStatus = 'LOADING';
    forkJoin(changes).subscribe({
      next: () => {
        changedRows.forEach((row: FormGroup) => {
          row.get('identifier').disable();
        });
        this.entitiesForm.markAsPristine();
        this.entitiesToDelete = [];
        this.savingEntitiesStatus = 'DONE';
        this.updateLocalEntities(
          (this.entitiesForm.get('entities') as FormArray).getRawValue()
        );
        this.changeRef.markForCheck();
        this.resetSavingStatus();
        this.formChanged = false;
        this.initialFormValue = this.entitiesForm.getRawValue();
      },
      error: () => {
        this.savingEntitiesStatus = 'ERROR';
        this.changeRef.markForCheck();
        this.resetSavingStatus();
      }
    });
  }

  public resetSavingStatus() {
    setTimeout(() => {
      this.savingEntitiesStatus = '';
      this.changeRef.markForCheck();
    }, 1500);
  }

  public updateScroll() {
    let e = this.divToScroll.nativeElement;
    e.scrollTop = e.scrollHeight;
  }

  public deleteEntity() {
    const selectedEntity = this.gridApi.getSelectedNodes()[0];
    const entityToDelete = (this.entitiesForm.controls.entities as FormArray)
      .controls[selectedEntity.data.rowIndex] as FormGroup;

    if (entityToDelete.get('identifier').disabled) {
      // if what is to be deleted has the identifier, disabled it has been saved
      this.entitiesToDelete.push(entityToDelete.getRawValue());
    }

    this.gridApi.applyTransaction({
      remove: [selectedEntity.data]
    });

    this.gridApi.forEachNode((x, index: number) => {
      x.updateData({ ...x.data, rowIndex: index });
    });

    setTimeout(() => {
      (this.entitiesForm.controls.entities as FormArray).removeAt(
        selectedEntity.data.rowIndex
      );
    }, 0);
    this.changeRef.markForCheck();
    this.selectedEntity = null;
  }

  public get allowedToSave(): boolean {
    return (
      this.formChanged &&
      this.savingEntitiesStatus !== 'LOADING' &&
      this.entitiesForm.valid &&
      ((!this.entitiesForm.pristine &&
        (this.entitiesForm.get('entities') as FormArray).length > 0) ||
        this.entitiesToDelete.length > 0)
    );
  }

  public identifierValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    // validator makes sure that the identifier's are unique
    if (!control.parent?.parent) {
      return null;
    }

    const previousValue = control.parent.value.identifier;
    const reportsArray = control.parent.parent.controls as FormGroup[];

    let duplicateIdentifiers = false;

    // find the number of current duplicates and if the value has changed
    // make the duplicates update their validity to see if they are valid now.
    reportsArray.forEach(report => {
      const ident = report.get('identifier');
      if (control.value !== previousValue && ident.value === previousValue) {
        // update the validity of other identifiers that had this
        // control's previous value
        ident.updateValueAndValidity();
      }
      if (ident.value === control.value && ident !== control) {
        duplicateIdentifiers = true;
      }
    });
    return duplicateIdentifiers
      ? { invalidEntry: { value: control.value } }
      : null;
  }

  public modalChanged(event: boolean) {
    this.tradesCaptureService.setShowEntities = event;
  }

  public get showEntities() {
    return this.tradesCaptureService.showEntities;
  }

  public gridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setRowData([]);
    this.gridApi.showLoadingOverlay();

    this.tradesCaptureService.getEntities({ raw: true }).subscribe({
      next: (entities: Entity[]) => {
        this.updateLocalEntities(entities);
        entities.forEach(entity => {
          (this.entitiesForm.get('entities') as FormArray).push(
            this.createEntityForm(entity)
          );
        });

        entities.forEach((x, i: number) => {
          x.rowIndex = i;
          if (x.isMemberOfParentGroup !== undefined) {
            x.isMemberOfParentGroup = isTrueLike(x.isMemberOfParentGroup);
          }
        });
        entities.forEach(entity => {
          const pbs = entity.primeBrokers;
          if (!pbs) {
            return;
          }
          entity.primeBrokers = pbs.map(pb => {
            return this.entityMapper.getDisplayEntity(pb);
          });
        });
        this.gridApi.setRowData(entities);
        this.loadingEntities = false;
        this.initialFormValue = this.entitiesForm.getRawValue();
        this.changeRef.markForCheck();
        this.gridApi.sizeColumnsToFit();
      }
    });
  }

  public onSelectionChanged(): void {
    const selectedRows = this.gridApi.getSelectedNodes();
    if (selectedRows.length) {
      this.selectedEntity = selectedRows[0].data;
    }
  }

  public updateRow(columnId: string, value: boolean, rowId: string): void {
    const row = this.gridApi.getRowNode(rowId);
    const rowDataClone = clone(row.data);
    rowDataClone[columnId] = value;
    row.updateData(rowDataClone);
  }

  private updateLocalEntities(entities: Entity[]) {
    this.legService.updateEntities(entities);
  }

  private subAccountToFormControl(
    field: SubAccount,
    headers: Header[]
  ): FormGroup {
    const fieldData = {};
    headers.forEach(header => {
      const validators = [];
      if (header.optional === false) {
        validators.push(Validators.required);
      }

      const currentValue = field?.[header.field];
      let value = null;
      switch (header.valueFormatter) {
        case 'boolean':
          value = isTrueLike(currentValue);
          break;
        default:
          value = currentValue ?? '';
          break;
      }

      fieldData[header.field] = new FormControl(value, validators);
    });
    return new FormGroup(fieldData);
  }

  private handleDetailCellRendererParams(): void {
    this.detailCellRendererParams = masterGridParams => ({
      context: {
        node: masterGridParams,
        form: this.entitiesForm,
        headers: this.subAccountsHeaders,
        subAccountToFormConverter: this.subAccountToFormControl,
        defaultColDef: this.defaultColDef
      }
    });
  }

  private handleMainHeaders(
    agGridConfig: AgGridConfig[],
    readonly = false
  ): void {
    this.originalHeaders = (
      agGridConfig.find(entry => entry.identifier === 'entitiesHeaders')
        ?.headers as Header[]
    ).filter(header => header.field !== 'subAccounts');

    this.columnDefs = clone(this.originalHeaders);
    this.columnDefs.forEach(col => {
      if (col.sortable) {
        col.comparator = customComparator;
      }

      if (col.valueFormatter === 'boolean') {
        col.cellRenderer = ReusableGridTemplateComponent;
        col.cellRendererParams = { ngTemplate: this.entityComponent };
        delete col.valueFormatter; // else creates ag grid warnings
      } else if (readonly) {
        col.editable = false;
      } else {
        col.editable = true;
        col.cellEditorSelector = params => {
          return {
            component: TextBoxEditorComponent,
            params: {
              context: {
                form: this.entitiesArray[params.data.rowIndex]
              }
            }
          };
        };
      }
    });

    this.columnDefs.unshift({
      field: '',
      sortable: false,
      cellRenderer: 'agGroupCellRenderer',
      width: 40
    } as Header);
  }

  private handleSubAccountsHeaders(agGridConfig: AgGridConfig[]): void {
    this.subAccountsHeaders = agGridConfig.find(
      y => y.identifier === 'subAccountsHeaders'
    )?.headers as Header[];
  }
}

const customComparator = (valueA, valueB) => {
  return valueA
    ?.toString()
    .toLowerCase()
    .localeCompare(valueB?.toString().toLowerCase());
};

function suppressNavigation(params: SuppressKeyboardEventParams): boolean {
  var key = params.event.key;
  return (
    key === 'ArrowRight' ||
    key === 'ArrowLeft' ||
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'Delete'
  );
}
